/**
 * Estimated Taxes Page
 * Track quarterly estimated tax payments (federal + state)
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  PiggyBank,
  Calendar,
  CheckCircle2,
  AlertCircle,
  Clock,
  DollarSign,
  Plus,
  Edit3,
} from 'lucide-react';

interface TaxYear {
  id: string;
  businessProfileId: string;
  businessProfileName: string;
  taxYear: number;
  filingStatus: string;
  entityType: string;
  state: string | null;
  federalTaxRate: number | null;
  stateTaxRate: number | null;
  selfEmploymentRate: number;
  notes: string | null;
  isFinalized: boolean;
}

interface EstimatedPayment {
  id: string;
  taxYearId: string;
  quarter: number;
  dueDate: string;
  federalAmount: number;
  stateAmount: number;
  federalPaid: number;
  statePaid: number;
  federalPaidDate: string | null;
  statePaidDate: string | null;
  federalConfirmation: string | null;
  stateConfirmation: string | null;
  notes: string | null;
}

interface BusinessProfile {
  id: string;
  name: string;
}

const FILING_STATUSES = [
  { value: 'single', label: 'Single' },
  { value: 'married_filing_jointly', label: 'Married Filing Jointly' },
  { value: 'married_filing_separately', label: 'Married Filing Separately' },
  { value: 'head_of_household', label: 'Head of Household' },
  { value: 'qualifying_widow', label: 'Qualifying Widow(er)' },
];

const ENTITY_TYPES = [
  { value: 'sole_proprietorship', label: 'Sole Proprietorship' },
  { value: 'llc_single', label: 'LLC (Single Member)' },
  { value: 'llc_multi', label: 'LLC (Multi Member)' },
  { value: 's_corp', label: 'S Corporation' },
  { value: 'c_corp', label: 'C Corporation' },
  { value: 'partnership', label: 'Partnership' },
];

const currentYear = new Date().getFullYear();

export function EstimatedTaxesPage() {
  const queryClient = useQueryClient();
  const [selectedTaxYearId, setSelectedTaxYearId] = useState<string>('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [editingPayment, setEditingPayment] = useState<EstimatedPayment | null>(null);

  // Create tax year form state
  const [newYear, setNewYear] = useState({
    businessProfileId: '',
    taxYear: currentYear,
    filingStatus: 'single',
    entityType: 'sole_proprietorship',
    state: '',
  });

  // Payment edit form state
  const [paymentForm, setPaymentForm] = useState({
    federalAmount: '',
    stateAmount: '',
    federalPaid: '',
    statePaid: '',
    federalPaidDate: '',
    statePaidDate: '',
    federalConfirmation: '',
    stateConfirmation: '',
    notes: '',
  });

  const { data: profilesData } = useQuery({
    queryKey: ['business-profiles'],
    queryFn: () => api.getBusinessProfiles(),
  });
  const profiles = (profilesData?.data || []) as BusinessProfile[];

  const { data: taxYearsData } = useQuery({
    queryKey: ['tax-years'],
    queryFn: () => api.getTaxYears(),
  });
  const taxYears = (taxYearsData?.data || []) as TaxYear[];

  const selectedTaxYear = taxYears.find(ty => ty.id === selectedTaxYearId);

  const { data: paymentsData, isLoading: paymentsLoading } = useQuery({
    queryKey: ['estimated-payments', selectedTaxYearId],
    queryFn: () => api.getEstimatedPayments(selectedTaxYearId),
    enabled: !!selectedTaxYearId,
  });
  const payments = (paymentsData?.data || []) as EstimatedPayment[];

  const createYearMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createTaxYear(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tax-years'] });
      setShowCreateDialog(false);
    },
  });

  const updatePaymentMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.updateEstimatedPayment(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimated-payments'] });
      setShowPaymentDialog(false);
      setEditingPayment(null);
    },
  });

  const handleCreateYear = () => {
    createYearMutation.mutate(newYear);
  };

  const handleEditPayment = (payment: EstimatedPayment) => {
    setEditingPayment(payment);
    setPaymentForm({
      federalAmount: payment.federalAmount.toString(),
      stateAmount: payment.stateAmount.toString(),
      federalPaid: payment.federalPaid.toString(),
      statePaid: payment.statePaid.toString(),
      federalPaidDate: payment.federalPaidDate || '',
      statePaidDate: payment.statePaidDate || '',
      federalConfirmation: payment.federalConfirmation || '',
      stateConfirmation: payment.stateConfirmation || '',
      notes: payment.notes || '',
    });
    setShowPaymentDialog(true);
  };

  const handleSavePayment = () => {
    if (!editingPayment) return;
    updatePaymentMutation.mutate({
      id: editingPayment.id,
      data: {
        federalAmount: parseFloat(paymentForm.federalAmount) || 0,
        stateAmount: parseFloat(paymentForm.stateAmount) || 0,
        federalPaid: parseFloat(paymentForm.federalPaid) || 0,
        statePaid: parseFloat(paymentForm.statePaid) || 0,
        federalPaidDate: paymentForm.federalPaidDate || null,
        statePaidDate: paymentForm.statePaidDate || null,
        federalConfirmation: paymentForm.federalConfirmation || null,
        stateConfirmation: paymentForm.stateConfirmation || null,
        notes: paymentForm.notes || null,
      },
    });
  };

  const getQuarterStatus = (payment: EstimatedPayment) => {
    const totalDue = payment.federalAmount + payment.stateAmount;
    const totalPaid = payment.federalPaid + payment.statePaid;
    const dueDate = new Date(payment.dueDate);
    const now = new Date();

    if (totalDue === 0 && totalPaid === 0) return { label: 'Not Set', variant: 'secondary' as const, icon: Clock };
    if (totalPaid >= totalDue) return { label: 'Paid', variant: 'success' as const, icon: CheckCircle2 };
    if (now > dueDate) return { label: 'Overdue', variant: 'destructive' as const, icon: AlertCircle };
    return { label: 'Pending', variant: 'warning' as const, icon: Clock };
  };

  const totalFederalDue = payments.reduce((s, p) => s + p.federalAmount, 0);
  const totalStateDue = payments.reduce((s, p) => s + p.stateAmount, 0);
  const totalFederalPaid = payments.reduce((s, p) => s + p.federalPaid, 0);
  const totalStatePaid = payments.reduce((s, p) => s + p.statePaid, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Estimated Tax Payments</h1>
          <p className="text-muted-foreground">Track quarterly federal and state estimated tax payments</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedTaxYearId} onValueChange={setSelectedTaxYearId}>
            <SelectTrigger className="w-[280px]">
              <SelectValue placeholder="Select a tax year..." />
            </SelectTrigger>
            <SelectContent>
              {taxYears.map(ty => (
                <SelectItem key={ty.id} value={ty.id}>
                  TY {ty.taxYear} - {ty.businessProfileName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Tax Year
          </Button>
        </div>
      </div>

      {!selectedTaxYearId ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <PiggyBank className="h-12 w-12 text-muted-foreground/50 mb-3" />
            <p className="text-lg font-medium">Select or Create a Tax Year</p>
            <p className="text-muted-foreground text-sm mt-1">
              Create a tax year for each business to track quarterly estimated tax payments
            </p>
            <Button className="mt-4" onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Tax Year
            </Button>
          </CardContent>
        </Card>
      ) : paymentsLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <>
          {/* Tax Year Info */}
          {selectedTaxYear && (
            <Card>
              <CardContent className="pt-6">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div>
                    <div className="text-xs text-muted-foreground uppercase">Business</div>
                    <div className="font-medium">{selectedTaxYear.businessProfileName}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground uppercase">Filing Status</div>
                    <div className="font-medium capitalize">{selectedTaxYear.filingStatus.replace(/_/g, ' ')}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground uppercase">Entity Type</div>
                    <div className="font-medium capitalize">{selectedTaxYear.entityType.replace(/_/g, ' ')}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground uppercase">State</div>
                    <div className="font-medium">{selectedTaxYear.state || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground uppercase">SE Tax Rate</div>
                    <div className="font-medium">{selectedTaxYear.selfEmploymentRate}%</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Federal Due</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(totalFederalDue)}</div>
                <p className="text-xs text-muted-foreground">{formatCurrency(totalFederalPaid)} paid</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">State Due</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(totalStateDue)}</div>
                <p className="text-xs text-muted-foreground">{formatCurrency(totalStatePaid)} paid</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Paid</CardTitle>
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-emerald-600">
                  {formatCurrency(totalFederalPaid + totalStatePaid)}
                </div>
                <p className="text-xs text-muted-foreground">
                  of {formatCurrency(totalFederalDue + totalStateDue)} total
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Remaining</CardTitle>
                <AlertCircle className="h-4 w-4 text-destructive" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-destructive">
                  {formatCurrency(Math.max(0, (totalFederalDue + totalStateDue) - (totalFederalPaid + totalStatePaid)))}
                </div>
                <p className="text-xs text-muted-foreground">still owed</p>
              </CardContent>
            </Card>
          </div>

          {/* Quarterly Payments */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {payments.map((payment) => {
              const status = getQuarterStatus(payment);
              const StatusIcon = status.icon;
              return (
                <Card key={payment.id}>
                  <CardHeader className="flex flex-row items-center justify-between pb-3">
                    <div>
                      <CardTitle className="text-lg">Q{payment.quarter} Estimated Payment</CardTitle>
                      <CardDescription className="flex items-center gap-1 mt-1">
                        <Calendar className="h-3 w-3" />
                        Due: {formatDate(payment.dueDate)}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={status.variant}>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {status.label}
                      </Badge>
                      <Button variant="ghost" size="sm" onClick={() => handleEditPayment(payment)}>
                        <Edit3 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <div className="text-xs text-muted-foreground uppercase tracking-wide">Federal</div>
                        <div className="flex justify-between">
                          <span className="text-sm">Estimated</span>
                          <span className="font-medium">{formatCurrency(payment.federalAmount)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-emerald-600">Paid</span>
                          <span className="font-medium text-emerald-600">{formatCurrency(payment.federalPaid)}</span>
                        </div>
                        {payment.federalConfirmation && (
                          <div className="text-xs text-muted-foreground">
                            Conf: {payment.federalConfirmation}
                          </div>
                        )}
                      </div>
                      <div className="space-y-2">
                        <div className="text-xs text-muted-foreground uppercase tracking-wide">State</div>
                        <div className="flex justify-between">
                          <span className="text-sm">Estimated</span>
                          <span className="font-medium">{formatCurrency(payment.stateAmount)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-emerald-600">Paid</span>
                          <span className="font-medium text-emerald-600">{formatCurrency(payment.statePaid)}</span>
                        </div>
                        {payment.stateConfirmation && (
                          <div className="text-xs text-muted-foreground">
                            Conf: {payment.stateConfirmation}
                          </div>
                        )}
                      </div>
                    </div>
                    {payment.notes && (
                      <div className="mt-3 text-xs text-muted-foreground border-t pt-2">{payment.notes}</div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {/* Create Tax Year Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Tax Year</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Business Profile</Label>
              <Select value={newYear.businessProfileId} onValueChange={(v) => setNewYear({ ...newYear, businessProfileId: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select business..." />
                </SelectTrigger>
                <SelectContent>
                  {profiles.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tax Year</Label>
              <Select value={newYear.taxYear.toString()} onValueChange={(v) => setNewYear({ ...newYear, taxYear: parseInt(v) })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[currentYear + 1, currentYear, currentYear - 1, currentYear - 2].map(y => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Filing Status</Label>
              <Select value={newYear.filingStatus} onValueChange={(v) => setNewYear({ ...newYear, filingStatus: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FILING_STATUSES.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Entity Type</Label>
              <Select value={newYear.entityType} onValueChange={(v) => setNewYear({ ...newYear, entityType: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ENTITY_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>State (2-letter code)</Label>
              <Input
                value={newYear.state}
                onChange={(e) => setNewYear({ ...newYear, state: e.target.value.toUpperCase().slice(0, 2) })}
                placeholder="TX"
                maxLength={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateYear} disabled={!newYear.businessProfileId || createYearMutation.isPending}>
              Create Tax Year
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Q{editingPayment?.quarter} Estimated Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Federal Amount Due</Label>
                <Input
                  type="number"
                  value={paymentForm.federalAmount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, federalAmount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>State Amount Due</Label>
                <Input
                  type="number"
                  value={paymentForm.stateAmount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, stateAmount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Federal Paid</Label>
                <Input
                  type="number"
                  value={paymentForm.federalPaid}
                  onChange={(e) => setPaymentForm({ ...paymentForm, federalPaid: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>State Paid</Label>
                <Input
                  type="number"
                  value={paymentForm.statePaid}
                  onChange={(e) => setPaymentForm({ ...paymentForm, statePaid: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Federal Paid Date</Label>
                <Input
                  type="date"
                  value={paymentForm.federalPaidDate}
                  onChange={(e) => setPaymentForm({ ...paymentForm, federalPaidDate: e.target.value })}
                />
              </div>
              <div>
                <Label>State Paid Date</Label>
                <Input
                  type="date"
                  value={paymentForm.statePaidDate}
                  onChange={(e) => setPaymentForm({ ...paymentForm, statePaidDate: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Federal Confirmation #</Label>
                <Input
                  value={paymentForm.federalConfirmation}
                  onChange={(e) => setPaymentForm({ ...paymentForm, federalConfirmation: e.target.value })}
                  placeholder="Confirmation number"
                />
              </div>
              <div>
                <Label>State Confirmation #</Label>
                <Input
                  value={paymentForm.stateConfirmation}
                  onChange={(e) => setPaymentForm({ ...paymentForm, stateConfirmation: e.target.value })}
                  placeholder="Confirmation number"
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Input
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                placeholder="Optional notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>Cancel</Button>
            <Button onClick={handleSavePayment} disabled={updatePaymentMutation.isPending}>
              Save Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
